import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Divider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { simulate } from '../services/api/simulator';
import { useTheme } from '../context/ThemeContext';

const Simulator = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    airDensity: '',
    cyclistWeight: '',
    luggageWeight: '',
    rollingResistanceCoefficient: '',
    mechanicalEfficiency: '',
    velocityKmH: '',
    power: ''
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { actualTheme } = useTheme();

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const simulationParams = {
        airDensity: parseFloat(formData.airDensity),
        cyclistWeight: parseFloat(formData.cyclistWeight),
        luggageWeight: parseFloat(formData.luggageWeight),
        rollingResistanceCoefficient: parseFloat(formData.rollingResistanceCoefficient),
        mechanicalEfficiency: parseFloat(formData.mechanicalEfficiency),
        velocityKmH: parseFloat(formData.velocityKmH),
        power: parseFloat(formData.power)
      };

      // Validate that all fields are filled and are valid numbers
      if (Object.values(simulationParams).some((value) => isNaN(value))) {
        throw new Error(t('simulator.invalidInput'));
      }

      const response = await simulate(simulationParams);
      setResult(response.data);
    } catch (error) {
      setError(error.message || t('simulator.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      airDensity: '',
      cyclistWeight: '',
      luggageWeight: '',
      rollingResistanceCoefficient: '',
      mechanicalEfficiency: '',
      velocityKmH: '',
      power: ''
    });
    setResult(null);
    setError(null);
  };

  const isFormValid = () =>
    Object.values(formData).every(
      (value) => value !== '' && !isNaN(parseFloat(value)) && parseFloat(value) >= 0
    );

  const textFieldStyles = {
    mb: 3,
    '& .MuiOutlinedInput-root': {
      backgroundColor: actualTheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
      '& .MuiInputBase-input': {
        color: actualTheme === 'dark' ? '#ffffff' : '#000000',
      },
    },
    '& .MuiInputLabel-root': {
      color: actualTheme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: actualTheme === 'dark' ? '#ffffff' : '#2196F3',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: actualTheme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
    },
  };

  return (
    <Container maxWidth="md" sx={{ mt: 12, mb: 4 }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          mb: 4,
          fontWeight: 300,
          letterSpacing: '0.1em',
          color: actualTheme === 'dark' ? '#ffffff' : '#000000',
        }}
      >
        {t('simulator.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={0}
        sx={{
          p: 4,
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label={t('simulator.airDensity')}
              type="number"
              inputProps={{ step: 'any' }}
              value={formData.airDensity}
              onChange={handleChange('airDensity')}
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label={t('simulator.cyclistWeight')}
              type="number"
              inputProps={{ step: 'any' }}
              value={formData.cyclistWeight}
              onChange={handleChange('cyclistWeight')}
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label={t('simulator.luggageWeight')}
              type="number"
              inputProps={{ step: 'any' }}
              value={formData.luggageWeight}
              onChange={handleChange('luggageWeight')}
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label={t('simulator.rollingResistanceCoefficient')}
              type="number"
              inputProps={{ step: 'any' }}
              value={formData.rollingResistanceCoefficient}
              onChange={handleChange('rollingResistanceCoefficient')}
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label={t('simulator.mechanicalEfficiency')}
              type="number"
              inputProps={{ step: 'any' }}
              value={formData.mechanicalEfficiency}
              onChange={handleChange('mechanicalEfficiency')}
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label={t('simulator.velocityKmH')}
              type="number"
              inputProps={{ step: 'any' }}
              value={formData.velocityKmH}
              onChange={handleChange('velocityKmH')}
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label={t('simulator.power')}
              type="number"
              inputProps={{ step: 'any' }}
              value={formData.power}
              onChange={handleChange('power')}
              sx={textFieldStyles}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={!isFormValid() || loading}
            sx={{
              textTransform: 'none',
              letterSpacing: '0.1em',
              fontWeight: 400,
              color: '#ffffff',
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #2196F3 60%, #21CBF3 90%)',
              },
              '&.Mui-disabled': {
                background: 'rgba(33, 150, 243, 0.5)',
                color: 'rgba(255, 255, 255, 0.5)',
              },
            }}
            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : null}
          >
            {t('simulator.simulate')}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleReset}
            sx={{
              textTransform: 'none',
              letterSpacing: '0.1em',
              fontWeight: 400,
              color: actualTheme === 'dark' ? '#ffffff' : '#2196F3',
              borderColor: actualTheme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(33, 150, 243, 0.5)',
            }}
          >
            {t('simulator.reset')}
          </Button>
        </Box>
      </Paper>

      {result && (
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: 4,
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontWeight: 300,
              letterSpacing: '0.1em',
              color: actualTheme === 'dark' ? '#ffffff' : '#000000',
            }}
          >
            {t('simulator.results')}
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ color: actualTheme === 'dark' ? '#ffffff' : '#000000' }}>
                <strong>{t('simulator.velocityMS')}</strong>: {result.velocityMS?.toFixed(2) || 'N/A'} m/s
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ color: actualTheme === 'dark' ? '#ffffff' : '#000000' }}>
                <strong>{t('simulator.totalMass')}</strong>: {result.totalMass?.toFixed(2) || 'N/A'} kg
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ color: actualTheme === 'dark' ? '#ffffff' : '#000000' }}>
                <strong>{t('simulator.rollingResistance')}</strong>: {result.rollingResistance?.toFixed(2) || 'N/A'} N
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ color: actualTheme === 'dark' ? '#ffffff' : '#000000' }}>
                <strong>{t('simulator.dragPower')}</strong>: {result.dragPower?.toFixed(2) || 'N/A'} W
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ color: actualTheme === 'dark' ? '#ffffff' : '#000000' }}>
                <strong>{t('simulator.frictionForce')}</strong>: {result.frictionForce?.toFixed(2) || 'N/A'} N
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1" sx={{ color: actualTheme === 'dark' ? '#ffffff' : '#000000' }}>
                <strong>{t('simulator.aerodynamicDragCoefficient')}</strong>: {result.aerodynamicDragCoefficient?.toFixed(2) || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Container>
  );
};

export default Simulator;